/// Port of mobile/lib/ui/pixel/pixel_toggle.dart.
///
/// Pass `label` for an inline title; omit it when a section header sits above,
/// which also switches the ON/OFF text to the larger size.

import { useThemeColors } from '@/settings/handler/settingsStore';
import { PixelContainer } from './PixelContainer';

const trackWidth = 52;
const trackHeight = 28;
const knobSize = 18;
const animDurationMs = 150;

export interface PixelToggleProps {
  value: boolean;
  onChange: (value: boolean) => void;
  label?: string;
  pixelScale?: number;
  radius?: number;
}

export function PixelToggle({
  value,
  onChange,
  label,
  pixelScale = 2,
  radius = 3,
}: PixelToggleProps) {
  const colors = useThemeColors();

  const statusOpacity = value ? 1 : label === undefined ? 0.55 : 0.4;
  const statusSize = label === undefined ? 20 : 16;

  return (
    <PixelContainer
      radius={radius}
      pixelScale={pixelScale}
      padding="8px 12px"
      backgroundColor={colors.surfaceContainerHighest}
      borderColor={colors.onSurface}
      onClick={() => onChange(!value)}
      ariaLabel={label}
      className="w-full"
    >
      <div className="flex w-full items-center gap-2">
        {label !== undefined && (
          <span className="flex-1 leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
            {label}
          </span>
        )}

        <span
          className={label === undefined ? 'flex-1 leading-none' : 'leading-none'}
          style={{
            fontSize: statusSize,
            color: colors.onSurface,
            opacity: statusOpacity,
            transition: `opacity ${animDurationMs}ms, font-size ${animDurationMs}ms`,
          }}
        >
          {value ? 'ON' : 'OFF'}
        </span>

        <PixelSwitch value={value} pixelScale={pixelScale} />
      </div>
    </PixelContainer>
  );
}

function PixelSwitch({ value, pixelScale }: { value: boolean; pixelScale: number }) {
  const colors = useThemeColors();

  const trackFill = value ? colors.onSurface : colors.surfaceContainerLow;
  const knobFill = value ? colors.surfaceContainerHighest : colors.onSurface;

  return (
    <span
      className="relative shrink-0"
      style={{ width: trackWidth, height: trackHeight }}
      role="switch"
      aria-checked={value}
    >
      <PixelContainer
        radius={2}
        pixelScale={pixelScale}
        padding="2px"
        backgroundColor={trackFill}
        borderColor={colors.onSurface}
        style={{ width: trackWidth, height: trackHeight }}
      >
        <span
          className="block"
          style={{
            width: knobSize,
            height: knobSize,
            // Track inset is pixelScale (chrome) + 2px (padding) on each side.
            transform: `translateX(${value ? trackWidth - knobSize - 2 * (pixelScale + 2) : 0}px)`,
            transition: `transform ${animDurationMs}ms ease-out`,
          }}
        >
          <PixelContainer
            radius={1}
            pixelScale={pixelScale}
            padding="0px"
            backgroundColor={knobFill}
            borderColor={knobFill}
            style={{ width: knobSize, height: knobSize }}
          >
            <span className="block" style={{ width: knobSize - 2 * pixelScale, height: knobSize - 2 * pixelScale }} />
          </PixelContainer>
        </span>
      </PixelContainer>
    </span>
  );
}
